import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Lock,
  LogOut,
  Loader2,
  RefreshCcw,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Car,
} from "lucide-react";
import { adminApi, api, BUSINESS } from "@/lib/config";

const STORAGE_KEY = "otgg_admin_token";

const SERVICE_LABELS = {
  exterior_detail: "Exterior",
  interior_detail: "Interior",
  full_detail: "Full Detail",
  maintenance_clean: "Maintenance",
};

const VEHICLE_LABELS = { car: "Car", suv: "SUV", truck: "Truck" };
const TIER_LABELS = { basic: "Basic", mid: "Mid", premium: "Premium" };

const STATUS_OPTIONS = ["pending", "confirmed", "completed", "cancelled"];

const STATUS_STYLES = {
  pending: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  confirmed: "bg-zinc-200/10 text-zinc-100 border-zinc-400/40",
  completed: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  cancelled: "bg-zinc-700/40 text-zinc-500 border-zinc-700",
};

function LoginCard({ onAuth }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/admin/login", { password });
      localStorage.setItem(STORAGE_KEY, r.data.token);
      onAuth(r.data.token);
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#050505]">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-2xl border border-zinc-900 bg-zinc-950 p-8"
      >
        <Link to="/" className="flex items-center gap-3 mb-8">
          <img src={BUSINESS.logo} alt="OTGG" className="logo-blend h-10 w-10 object-contain" />
          <div>
            <div className="font-heading text-sm tracking-[0.15em] text-chrome">OTGG ADMIN</div>
            <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase">Mobile Detailing</div>
          </div>
        </Link>
        <h1 className="font-heading text-2xl text-white mb-2 tracking-tight">Sign in</h1>
        <p className="text-zinc-500 text-sm mb-8">Enter the admin password to view bookings.</p>
        <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Password</label>
        <div className="relative">
          <Lock size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className="w-full h-12 pl-10 pr-4 rounded-lg bg-black border border-zinc-800 text-white focus:border-white focus:outline-none"
            autoFocus
          />
        </div>
        <button
          type="submit"
          disabled={loading || !password}
          data-testid="admin-login-btn"
          className="mt-6 w-full h-12 rounded-full bg-white text-black font-medium hover:bg-zinc-200 disabled:opacity-30 transition-colors inline-flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : "Sign in"}
        </button>
      </form>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY));
  const [bookings, setBookings] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0 });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [updating, setUpdating] = useState(null);
  const [tab, setTab] = useState("bookings"); // bookings | fleet

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const client = adminApi(token);
      const [b, s, f] = await Promise.all([
        client.get("/admin/bookings"),
        client.get("/admin/stats"),
        client.get("/admin/fleet-inquiries"),
      ]);
      setBookings(b.data);
      setStats(s.data);
      setFleet(f.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(STORAGE_KEY);
        setToken(null);
        toast.error("Session expired. Sign in again.");
      } else {
        toast.error("Couldn't load data.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const updateFleetStatus = async (id, status) => {
    setUpdating(id);
    try {
      await adminApi(token).patch(`/admin/fleet-inquiries/${id}`, { status });
      toast.success(`Marked ${status}.`);
      load();
    } catch {
      toast.error("Update failed.");
    } finally {
      setUpdating(null);
    }
  };

  const updateStatus = async (id, status) => {
    setUpdating(id);
    try {
      const client = adminApi(token);
      await client.patch(`/admin/bookings/${id}`, { status });
      toast.success(`Marked ${status}.`);
      load();
    } catch {
      toast.error("Update failed.");
    } finally {
      setUpdating(null);
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
  };

  if (!token) return <LoginCard onAuth={setToken} />;

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#050505] text-zinc-200">
      <header className="sticky top-0 z-40 backdrop-blur-2xl bg-black/70 border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={BUSINESS.logo} alt="OTGG" className="logo-blend h-10 w-10 object-contain" />
            <div className="leading-tight">
              <div className="font-heading text-sm tracking-[0.2em] text-chrome">OTGG ADMIN</div>
              <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Bookings</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-zinc-800 text-zinc-300 hover:border-white hover:text-white text-sm transition-colors">
              <RefreshCcw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 h-10 px-4 rounded-full text-zinc-400 hover:text-white text-sm transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        {/* Tabs */}
        <div className="flex items-center gap-2 mb-8">
          <button onClick={() => setTab("bookings")} data-testid="tab-bookings" className={`h-10 px-5 rounded-full text-sm font-medium transition-colors ${tab === "bookings" ? "bg-white text-black" : "border border-zinc-800 text-zinc-300 hover:border-zinc-600"}`}>Bookings</button>
          <button onClick={() => setTab("fleet")} data-testid="tab-fleet" className={`h-10 px-5 rounded-full text-sm font-medium transition-colors ${tab === "fleet" ? "bg-white text-black" : "border border-zinc-800 text-zinc-300 hover:border-zinc-600"}`}>Fleet Inquiries {fleet.length > 0 && <span className="ml-1 opacity-60">({fleet.length})</span>}</button>
        </div>

        {tab === "bookings" && (
        <>
        {/* Stats */}
        <div data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
          {[
            { label: "Total", value: stats.total, key: "all" },
            { label: "Pending", value: stats.pending, key: "pending" },
            { label: "Confirmed", value: stats.confirmed, key: "confirmed" },
            { label: "Completed", value: stats.completed, key: "completed" },
          ].map((s) => (
            <button
              key={s.label}
              onClick={() => setFilter(s.key)}
              data-testid={`stat-${s.key}`}
              className={`text-left rounded-2xl border p-6 transition-colors ${
                filter === s.key ? "border-white bg-zinc-950" : "border-zinc-900 bg-zinc-950/40 hover:border-zinc-700"
              }`}
            >
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">{s.label}</div>
              <div className="font-heading text-4xl text-chrome">{s.value}</div>
            </button>
          ))}
          <div data-testid="stat-paid" className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-400/70 mb-2">Paid online</div>
            <div className="font-heading text-4xl text-emerald-300">{stats.paid ?? 0}</div>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-zinc-900 bg-zinc-950/60 overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-900 flex items-center justify-between">
            <h2 className="font-heading text-xl text-white tracking-tight">Bookings</h2>
            <div className="text-xs text-zinc-500">{filtered.length} of {bookings.length}</div>
          </div>

          {loading && bookings.length === 0 ? (
            <div className="p-16 text-center text-zinc-500"><Loader2 className="mx-auto animate-spin mb-3" size={20} />Loading bookings…</div>
          ) : filtered.length === 0 ? (
            <div data-testid="admin-empty" className="p-16 text-center text-zinc-500">No bookings yet.</div>
          ) : (
            <ul className="divide-y divide-zinc-900">
              {filtered.map((b) => (
                <li key={b.id} data-testid={`booking-row-${b.id}`} className="p-6 grid md:grid-cols-12 gap-6 hover:bg-zinc-950 transition-colors">
                  <div className="md:col-span-3">
                    <div className="font-heading text-lg text-white tracking-tight">{b.name}</div>
                    <a href={`tel:${b.phone}`} className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1.5 mt-1"><Phone size={12} /> {b.phone}</a>
                    <a href={`mailto:${b.email}`} className="block text-sm text-zinc-500 hover:text-white truncate mt-0.5 inline-flex items-center gap-1.5"><Mail size={12} /> {b.email}</a>
                  </div>
                  <div className="md:col-span-3 text-sm">
                    <div className="flex items-center gap-2 text-zinc-300"><Car size={14} className="text-zinc-500" />{b.vehicle_year} {b.vehicle_make} {b.vehicle_model}</div>
                    <div className="text-zinc-500 mt-1">{SERVICE_LABELS[b.service_type] || b.service_type} · {VEHICLE_LABELS[b.vehicle_type] || b.vehicle_type}{b.tier ? ` · ${TIER_LABELS[b.tier]}` : ""}</div>
                    {b.payment_status === "paid" && <div className="text-emerald-400 text-xs mt-1">✓ Paid ${b.paid_amount}</div>}
                  </div>
                  <div className="md:col-span-3 text-sm">
                    <div className="flex items-center gap-2 text-zinc-300"><Calendar size={14} className="text-zinc-500" />{b.preferred_date} · <span className="capitalize">{b.preferred_time}</span></div>
                    <div className="flex items-start gap-2 text-zinc-500 mt-1">
                      <MapPin size={14} className="text-zinc-600 mt-0.5 shrink-0" />
                      <span className="text-xs">{b.street_address}, {b.city} {b.state} {b.zip_code}{!b.in_service_area && <span className="text-amber-400"> · out of area</span>}</span>
                    </div>
                    {b.notes && <div className="mt-2 text-xs text-zinc-500 italic">"{b.notes}"</div>}
                  </div>
                  <div className="md:col-span-3 flex md:justify-end items-start gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border ${STATUS_STYLES[b.status] || ""}`}>
                      {b.status}
                    </span>
                    <select
                      value={b.status}
                      disabled={updating === b.id}
                      onChange={(e) => updateStatus(b.id, e.target.value)}
                      data-testid={`status-select-${b.id}`}
                      className="h-9 px-3 rounded-lg bg-black border border-zinc-800 text-xs text-zinc-300 hover:border-zinc-600 focus:border-white focus:outline-none"
                    >
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        </>
        )}

        {tab === "fleet" && (
          <div className="rounded-2xl border border-zinc-900 bg-zinc-950/60 overflow-hidden" data-testid="fleet-table">
            <div className="px-6 py-4 border-b border-zinc-900 flex items-center justify-between">
              <h2 className="font-heading text-xl text-white tracking-tight">Fleet Inquiries</h2>
              <div className="text-xs text-zinc-500">{fleet.length} total</div>
            </div>
            {fleet.length === 0 ? (
              <div data-testid="fleet-empty" className="p-16 text-center text-zinc-500">No fleet inquiries yet.</div>
            ) : (
              <ul className="divide-y divide-zinc-900">
                {fleet.map((f) => (
                  <li key={f.id} data-testid={`fleet-row-${f.id}`} className="p-6 grid md:grid-cols-12 gap-6 hover:bg-zinc-950 transition-colors">
                    <div className="md:col-span-4">
                      <div className="font-heading text-lg text-white tracking-tight">{f.company_name}</div>
                      <div className="text-xs text-zinc-500 uppercase tracking-wide mt-0.5">{f.business_type} · {f.fleet_size}</div>
                      <div className="text-sm text-zinc-400 mt-1">{f.contact_name}</div>
                    </div>
                    <div className="md:col-span-4 text-sm">
                      <a href={`tel:${f.phone}`} className="text-zinc-400 hover:text-white inline-flex items-center gap-1.5"><Phone size={12} /> {f.phone}</a>
                      <a href={`mailto:${f.email}`} className="block text-zinc-500 hover:text-white mt-0.5 inline-flex items-center gap-1.5"><Mail size={12} /> {f.email}</a>
                      {f.service_interest && <div className="text-zinc-400 mt-1">{f.service_interest}</div>}
                      {f.message && <div className="text-xs text-zinc-500 italic mt-1">"{f.message}"</div>}
                    </div>
                    <div className="md:col-span-4 flex md:justify-end items-start gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border border-zinc-700 text-zinc-300">{f.status}</span>
                      <select
                        value={f.status}
                        disabled={updating === f.id}
                        onChange={(e) => updateFleetStatus(f.id, e.target.value)}
                        data-testid={`fleet-status-${f.id}`}
                        className="h-9 px-3 rounded-lg bg-black border border-zinc-800 text-xs text-zinc-300 hover:border-zinc-600 focus:border-white focus:outline-none"
                      >
                        {["new", "contacted", "won", "closed"].map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
